import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { AuthService } from '../auth';
import { Logout } from '../store/actions/auth.actions';
import { AppState } from '../store/states/app.state';
import { logAuthWarn } from '../auth/auth-debug.helpers';

@Injectable({
  providedIn: 'root'
})
export class SessionExpiredService {
  private sessionExpiredSubject = new BehaviorSubject<boolean>(false);
  public sessionExpired$ = this.sessionExpiredSubject.asObservable();

  /** Prevent flooding modal / logout when many requests fail with 401 */
  private endingSession = false;

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private authService: AuthService,
    private store: Store<AppState>
  ) {}

  showSessionExpiredModal(): void {
    this.modalService.dismissAll();
    this.sessionExpiredSubject.next(true);
  }

  /**
   * Force end of client session (token revoked, inactive user, auth rejected).
   * Cleares token + store and blocks further authenticated navigation.
   */
  forceSessionEnd(): void {
    if (this.endingSession) {
      return;
    }
    this.endingSession = true;
    logAuthWarn('session-expired: forceSessionEnd');

    try {
      this.authService.clearToken('session-expired: forceSessionEnd');
    } catch {
      /* ignore */
    }

    try {
      this.store.dispatch(new Logout());
    } catch {
      /* ignore */
    }

    try {
      this.modalService.dismissAll();
    } catch {
      /* ignore */
    }

    this.sessionExpiredSubject.next(true);

    // Give Angular a tick to settle store before leaving protected routes
    setTimeout(() => {
      this.router.navigate(['/login2']).finally(() => {
        this.endingSession = false;
      });
    }, 50);
  }

  handleLogin(): void {
    this.sessionExpiredSubject.next(false);
    this.endingSession = false;
    this.clearSession();
    this.router.navigate(['/login2']);
  }

  private clearSession(): void {
    logAuthWarn('session-expired: clearSession');
    try {
      this.authService.clearToken('session-expired: clearSession');
    } catch {
      /* ignore */
    }
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      sessionStorage.clear();
    }
  }
}
